import { BUSINESS_GROUP_CODES } from 'src/constants/business-group-constants';
import { COST_CENTER_SEGMENT_NAME, PRODUCT_LINE_SEGMENT_NAME } from 'src/constants/corp-segment-constants';
import { BUDGET_LEADER, BUDGET_OWNER, eLocalStorageKeys, FINANCE_ADMIN, FINANCE_OWNER } from 'src/constants/generic-constants';
import { BusinessGroupEntity } from 'src/models/AppContextModels';
import { CostCenterAndProductLineValuesPerBusinessGroup } from 'src/models/ForecastModels';
import {
  CorpSegmentAccess,
  CurrentUserAccessDetails,
  UserAccessEntity,
  UserAccessForCurrentBusinessGroup,
  UserDataClassifications
} from 'src/models/XptUsersModel';

/**
 * Gets access details for the current user based on various conditions
 * @param userAlias - User's alias/identifier
 * @param isDev - Whether application is running in development mode
 * @param currentUsersPermissionFromAllBusinessGroups - List of user permissions across business groups
 * @param activeBusinessGroups - List of active business groups
 * @param ccAndPLForAllBusinessGroups - Cost center and product line values for all business groups
 * @returns Object containing user access details and whether user was found
 */
export const getCurrentUserAccessDetails = (
  userAlias: string,
  isDev: boolean,
  userAccess: UserAccessEntity[],
  activeBusinessGroups: BusinessGroupEntity[],
  ccAndPLForAllBusinessGroups: CostCenterAndProductLineValuesPerBusinessGroup[]
): { currentUserAccessDetails: CurrentUserAccessDetails | null; currentUserNotFound: boolean } => {
  // Check for user access in localStorage (for development overrides)
  const localStorageAccess = getAccessFromLocalStorage(userAlias);
  if (localStorageAccess) return localStorageAccess;

  // Handle development environment
  if (isDev) {
    return accessForDevelopers(userAlias, activeBusinessGroups, ccAndPLForAllBusinessGroups);
  }

  // Handle case when user has no permissions
  if (userAccess.length === 0) {
    return { currentUserAccessDetails: null, currentUserNotFound: true };
  }

  // Access for non dev users
  return accessForNonDevUsers(userAlias, userAccess, activeBusinessGroups, ccAndPLForAllBusinessGroups);
};

/**
 * Retrieves user access details from localStorage
 */
const getAccessFromLocalStorage = (userAlias: string) => {
  const userAccessFromLocalStorage = localStorage.getItem(eLocalStorageKeys.UserAccessDetails);
  if (userAccessFromLocalStorage) {
    const parsedAccess = JSON.parse(userAccessFromLocalStorage) as CurrentUserAccessDetails;

    // an override with no business groups represents the "no access" state
    // this is similar to how it works for real users
    if (!parsedAccess.user_data_classifications || parsedAccess.user_data_classifications.length === 0) {
      return { currentUserAccessDetails: null, currentUserNotFound: true };
    }

    return {
      currentUserAccessDetails: {
        user_alias: userAlias,
        user_access_modified: true,
        user_data_classifications: parsedAccess.user_data_classifications
      },
      currentUserNotFound: false
    };
  }
  return null;
};

/**
 * Creates access details for dev users
 */
const accessForDevelopers = (
  userAlias: string,
  activeBusinessGroups: BusinessGroupEntity[],
  ccAndPLForAllBusinessGroups: CostCenterAndProductLineValuesPerBusinessGroup[]
) => {
  const userDataClassifications: UserDataClassifications[] = activeBusinessGroups.map((bg: BusinessGroupEntity) => {
    const businessGroupData: CostCenterAndProductLineValuesPerBusinessGroup | undefined = ccAndPLForAllBusinessGroups.find(
      (ccPerBusinessGroup) => ccPerBusinessGroup.dataClassificationId === bg.data_classification.data_classification_id
    );

    return {
      data_classification_id: bg.data_classification.data_classification_id!,
      data_classification_short_description: bg.data_classification.data_classification_short_description,
      user_type: FINANCE_ADMIN,
      corp_segment_access: [
        createSegmentAccess(COST_CENTER_SEGMENT_NAME, businessGroupData?.costCenters ?? []),
        createSegmentAccess(PRODUCT_LINE_SEGMENT_NAME, businessGroupData?.productLines ?? [])
      ]
    };
  });

  const currentUserAccessDetails: CurrentUserAccessDetails = {
    user_alias: userAlias,
    user_access_modified: false,
    user_data_classifications: userDataClassifications
  };
  return {
    currentUserAccessDetails: currentUserAccessDetails,
    currentUserNotFound: false
  };
};

/**
 * Creates segment access object
 */
const createSegmentAccess = (segmentName: string, values: string[]): CorpSegmentAccess => ({
  segment_name: segmentName,
  segment_value: values
});

/**
 * Creates access details for production environment
 */
const accessForNonDevUsers = (
  userAlias: string,
  userAccess: UserAccessEntity[],
  activeBusinessGroups: BusinessGroupEntity[],
  ccAndPLForAllBusinessGroups: CostCenterAndProductLineValuesPerBusinessGroup[]
) => {
  const userDataClassifications = userAccess
    .map((user) => processUserPermissions(user, activeBusinessGroups, ccAndPLForAllBusinessGroups))
    // Filter out records with data_classification_id: 0
    .filter((classification) => classification.data_classification_id !== 0);

  return {
    currentUserAccessDetails: {
      user_alias: userAlias,
      user_access_modified: false,
      user_data_classifications: userDataClassifications
    },
    currentUserNotFound: false
  };
};

/**
 * Processes individual user permissions
 */
export const processUserPermissions = (
  user: UserAccessEntity,
  activeBusinessGroups: BusinessGroupEntity[],
  ccAndPLForAllBusinessGroups: CostCenterAndProductLineValuesPerBusinessGroup[]
): UserDataClassifications => {
  const businessGroup = activeBusinessGroups.find((bg) => bg.data_classification.data_classification_id === user.data_classification_id);

  // Return null or skip if business group not found
  if (!businessGroup) {
    return { data_classification_id: 0, data_classification_short_description: '', user_type: FINANCE_ADMIN, corp_segment_access: [] };
  }

  const ccAndPLAccess = ccAndPLForAllBusinessGroups.find(
    (ccPerBusinessGroup) => ccPerBusinessGroup.dataClassificationId === businessGroup.data_classification.data_classification_id
  ) || { costCenters: [], productLines: [] };

  const isDMM = businessGroup.data_classification.data_classification_short_description === BUSINESS_GROUP_CODES.DMM;
  let corpSegmentAccess: CorpSegmentAccess[] = [];

  // For DMM, Cost Center will be default from Master (ccAndPLForAllBusinessGroups)
  // Product Lines will be from Access
  if (isDMM) {
    corpSegmentAccess = [...user.corp_segment_access, { segment_name: COST_CENTER_SEGMENT_NAME, segment_value: ccAndPLAccess.costCenters }];
  } else {
    corpSegmentAccess = [...user.corp_segment_access, { segment_name: PRODUCT_LINE_SEGMENT_NAME, segment_value: ccAndPLAccess.productLines }];
  }

  return {
    data_classification_id: user.data_classification_id!,
    data_classification_short_description: businessGroup.data_classification.data_classification_short_description,
    user_type: user.user_type!,
    corp_segment_access: corpSegmentAccess
  };
};

/**
 * Gets cost centers for a specific user
 */
export const getUserCostCenters = (userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup): string[] => {
  return getSegmentValues(userAccessForCurrentBusinessGroup, COST_CENTER_SEGMENT_NAME);
};

/**
 * Gets product lines for a specific user
 */
export const getUserProductLines = (userAccessForCurrentBusinessGroup: UserAccessForCurrentBusinessGroup): string[] => {
  return getSegmentValues(userAccessForCurrentBusinessGroup, PRODUCT_LINE_SEGMENT_NAME);
};

/**
 * Helper function to get segment values
 */
const getSegmentValues = (userAccess: UserAccessForCurrentBusinessGroup, segmentName: string): string[] => {
  return userAccess.corp_segment_access.filter((segment) => segment.segment_name === segmentName).flatMap((segment) => segment.segment_value);
};

/**
 * Gets cost centers for a specific user from user access entities list
 */
export const getUserCostCentersFromUserAccessEntitiesList = (
  userAlias: string,
  xptUsersOfCurrentDataClassification: UserAccessEntity[]
): string[] => {
  const userAccess = xptUsersOfCurrentDataClassification.find((user) => user.user_alias?.toLowerCase() === userAlias?.toLowerCase());
  return userAccess ? getSegmentValues(userAccess as unknown as UserAccessForCurrentBusinessGroup, COST_CENTER_SEGMENT_NAME) : [];
};

/**
 * Gets product lines for a specific user from user access entities list
 */
export const getUserProductLinesFromUserAccessEntitiesList = (
  userAlias: string,
  xptUsersOfCurrentDataClassification: UserAccessEntity[]
): string[] => {
  const userAccess = xptUsersOfCurrentDataClassification.find((user) => user.user_alias?.toLowerCase() === userAlias?.toLowerCase());
  return userAccess ? getSegmentValues(userAccess as unknown as UserAccessForCurrentBusinessGroup, PRODUCT_LINE_SEGMENT_NAME) : [];
};

/**
 * Converts current user data classification to user access for current business group
 */
export const currentUserToUserAccessForCurrentBusinessGroup = (
  currentUserAccessDataClassification: UserDataClassifications,
  userAlias: string
): UserAccessForCurrentBusinessGroup => {
  const userType = currentUserAccessDataClassification.user_type;

  return {
    user_alias: userAlias,
    data_classification_id: currentUserAccessDataClassification.data_classification_id,
    user_type: userType,
    corp_segment_access: currentUserAccessDataClassification.corp_segment_access,
    isFinanceAdminOrFinanceOwner: userType === FINANCE_ADMIN || userType === FINANCE_OWNER,
    isFinanceAdmin: userType === FINANCE_ADMIN,
    isFinanceOwner: userType === FINANCE_OWNER,
    isBudgetLeader: userType === BUDGET_LEADER,
    isBudgetOwner: userType === BUDGET_OWNER
  };
};
